import React from "react";
import { Database } from "lucide-react";
import { Link, useParams } from "wouter";

const DatabaseSchemaAnalysisPage: React.FC = () => {
  const { clientCode } = useParams<{ clientCode: string }>();
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="bg-shiftleft-50 dark:bg-shiftleft-900/30 rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Database className="w-7 h-7 text-shiftleft-700" />
            <h1 className="text-2xl font-bold text-shiftleft-900 dark:text-shiftleft-100">
              Database Schema Analysis
            </h1>
          </div>
          <div className="text-shiftleft-900 dark:text-shiftleft-200 text-sm space-y-2">
            <p>Database schema analysis is the process of examining the structure, relationships, and constraints of a database to ensure optimal design, performance, and maintainability. Key aspects include:</p>
            <ul className="list-disc pl-6">
              <li><b>Table Structure Review:</b> Analyze tables for normalization, appropriate data types, and indexing strategies.</li>
              <li><b>Relationships & Constraints:</b> Identify primary keys, foreign keys, and unique constraints to ensure data integrity and efficient joins.</li>
              <li><b>Index Analysis:</b> Evaluate existing indexes for coverage, redundancy, and performance impact on queries.</li>
              <li><b>Column Usage:</b> Detect unused or redundant columns, and assess opportunities for denormalization or partitioning.</li>
              <li><b>Naming Conventions:</b> Ensure consistent and descriptive naming for tables, columns, and constraints.</li>
              <li><b>Growth & Scalability:</b> Review table sizes, row counts, and growth trends to anticipate scaling challenges.</li>
              <li><b>Security & Compliance:</b> Check for sensitive data storage, encryption, and access controls.</li>
            </ul>
            <p>Regular schema analysis helps prevent data anomalies, improves query performance, and supports future application requirements.</p>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">See Also</h2>
          <Link href={`/clients/${clientCode}/arch-analysis`} className="text-shiftleft-700 hover:underline">
            ← Back to Architecture Analysis
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DatabaseSchemaAnalysisPage;
